import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import "./TypeaheadCombobox.component.css";

/**
 * Free-text combobox shared by the ACR editor product search and the Request an ACR vendor field.
 * Callers own the option list. Two modes:
 * - Editor (`openOnFocus` false): the list opens after the caller has options (type-to-search).
 *   An unmatched value is committed only through `extraOptions` (for example "Use unknown product").
 * - Vendor field (`openOnFocus` + `allowFreeText`): the list opens on focus, and blur/Tab keeps
 *   whatever was typed even when it is not in the list.
 */
export default function TypeaheadCombobox({
  id,
  listboxId,
  value,
  onChange,
  options,
  getOptionKey,
  getOptionDomId,
  renderOption,
  onSelect,
  extraOptions,
  loading,
  openOnFocus,
  allowFreeText,
  onCommitFreeText,
  onInputFocusChange,
  expanded,
  onExpandedChange,
  placeholder,
  disabled,
  inputClassName,
  className,
  listClassName,
  optionClassName,
  highlightedClassName,
  loadingClassName,
  noResultsLabel,
  maxLength,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  "aria-labelledby": ariaLabelledBy,
  "aria-required": ariaRequired,
}) {
  const reactId = useId();
  const resolvedListboxId = listboxId || `${id || reactId}-listbox`;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const blurTimerRef = useRef(null);
  const listRef = useRef(null);
  const isControlled = expanded !== undefined;
  const isOpen = isControlled ? expanded : uncontrolledOpen;

  const setOpen = useCallback((next) => {
    if (!isControlled) {
      setUncontrolledOpen(next);
    }
    if (onExpandedChange) {
      onExpandedChange(next);
    }
  }, [isControlled, onExpandedChange]);

  const items = useMemo(() => {
    const optionItems = options.map((option, index) => ({
      kind: "option",
      key: String(getOptionKey(option, index)),
      domId: getOptionDomId(option, index),
      option,
      className: "",
    }));
    const extraItems = extraOptions.map((extra) => ({
      kind: "extra",
      key: extra.id,
      domId: extra.id,
      extra,
      className: extra.className || "",
    }));
    return [...optionItems, ...extraItems];
  }, [extraOptions, getOptionDomId, getOptionKey, options]);

  const optionsKey = options.map((option, index) => getOptionKey(option, index)).join("\0");
  const extraKey = extraOptions.map((extra) => extra.id).join("\0");
  const selectableCount = options.length + extraOptions.length;

  useEffect(() => {
    // Highlight follows the option set (ids), not extra-option labels that change while typing.
    // Free-text mode leaves the highlight unset until the user arrows or hovers, so Enter
    // submits the typed value instead of the first suggestion.
    setHighlightedIndex(!allowFreeText && selectableCount > 0 ? 0 : -1);
  }, [allowFreeText, optionsKey, extraKey, selectableCount]);

  useEffect(() => {
    if (!isOpen || highlightedIndex < 0) return;
    const list = listRef.current;
    const option = list?.querySelector('[role="option"][aria-selected="true"]');
    if (!list || !option) return;
    const listRect = list.getBoundingClientRect();
    const optionRect = option.getBoundingClientRect();
    if (optionRect.top < listRect.top) {
      list.scrollTop -= listRect.top - optionRect.top;
    } else if (optionRect.bottom > listRect.bottom) {
      list.scrollTop += optionRect.bottom - listRect.bottom;
    }
  }, [highlightedIndex, isOpen]);

  useEffect(() => () => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
    }
  }, []);

  const clearBlurTimer = () => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
  };

  const commitFreeText = () => {
    if (allowFreeText && onCommitFreeText) {
      onCommitFreeText(value);
    }
  };

  const selectItem = (item) => {
    if (!item || loading) return;
    if (item.kind === "option") {
      onSelect(item.option);
    } else {
      item.extra.onSelect();
    }
    setOpen(false);
    setHighlightedIndex(-1);
  };

  const handleFocus = () => {
    clearBlurTimer();
    if (onInputFocusChange) onInputFocusChange(true);
    if (openOnFocus || options.length > 0) {
      setOpen(true);
    }
  };

  const handleBlur = () => {
    if (onInputFocusChange) onInputFocusChange(false);
    clearBlurTimer();
    blurTimerRef.current = setTimeout(() => {
      blurTimerRef.current = null;
      commitFreeText();
      setOpen(false);
      setHighlightedIndex(-1);
    }, 200);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Tab") {
      commitFreeText();
      return;
    }
    if (!isOpen || loading) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (items.length === 0) return;
      setHighlightedIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (items.length === 0) return;
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
    } else if (event.key === "Enter") {
      if (highlightedIndex >= 0 && items[highlightedIndex]) {
        event.preventDefault();
        selectItem(items[highlightedIndex]);
        return;
      }
      // Free text has no chosen row: let the parent form submit the typed value.
      if (!allowFreeText) {
        event.preventDefault();
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const activeDescendant = isOpen && !loading && highlightedIndex >= 0
    ? items[highlightedIndex]?.domId
    : undefined;
  const showNoResults = isOpen && !loading && items.length === 0 && Boolean(noResultsLabel);

  return (
    <div className={`typeahead-combobox ${className}`.trim()}>
      <input
        id={id}
        type="text"
        role="combobox"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        className={`typeahead-combobox__input ${inputClassName}`.trim()}
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={resolvedListboxId}
        aria-activedescendant={activeDescendant}
        maxLength={maxLength}
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy || undefined}
        aria-labelledby={ariaLabelledBy || undefined}
        aria-required={ariaRequired || undefined}
        onChange={(event) => {
          onChange(event.target.value);
          // A selection closes the list without moving focus. Typing again has to
          // reopen it; focus-only open would stay shut until the user leaves and returns.
          if (openOnFocus) {
            setOpen(true);
          }
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {isOpen && (loading || items.length > 0 || showNoResults) && (
        <ul
          id={resolvedListboxId}
          ref={listRef}
          role="listbox"
          className={`typeahead-combobox__list ${listClassName}`.trim()}
        >
          {loading ? (
            <li
              className={`typeahead-combobox__option typeahead-combobox__option--loading ${optionClassName} ${loadingClassName}`.trim()}
              role="option"
              aria-selected={false}
              aria-disabled="true"
            >
              Loading…
            </li>
          ) : null}
          {!loading && items.map((item, index) => {
            const highlighted = index === highlightedIndex;
            return (
              <li
                key={item.key}
                id={item.domId}
                role="option"
                aria-selected={highlighted}
                className={`typeahead-combobox__option ${optionClassName} ${item.className} ${highlighted ? `typeahead-combobox__option--highlighted ${highlightedClassName}` : ""}`.trim()}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectItem(item);
                }}
              >
                {item.kind === "option" ? renderOption(item.option, index) : item.extra.label}
              </li>
            );
          })}
          {showNoResults ? (
            <li
              className={`typeahead-combobox__option typeahead-combobox__option--empty ${optionClassName}`.trim()}
              role="option"
              aria-selected={false}
              aria-disabled="true"
            >
              {noResultsLabel}
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}

TypeaheadCombobox.propTypes = {
  id: PropTypes.string.isRequired,
  listboxId: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  getOptionKey: PropTypes.func,
  getOptionDomId: PropTypes.func,
  renderOption: PropTypes.func,
  onSelect: PropTypes.func,
  extraOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
    className: PropTypes.string,
  })),
  loading: PropTypes.bool,
  openOnFocus: PropTypes.bool,
  allowFreeText: PropTypes.bool,
  onCommitFreeText: PropTypes.func,
  onInputFocusChange: PropTypes.func,
  expanded: PropTypes.bool,
  onExpandedChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  inputClassName: PropTypes.string,
  className: PropTypes.string,
  listClassName: PropTypes.string,
  optionClassName: PropTypes.string,
  highlightedClassName: PropTypes.string,
  loadingClassName: PropTypes.string,
  noResultsLabel: PropTypes.string,
  maxLength: PropTypes.number,
  "aria-invalid": PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  "aria-describedby": PropTypes.string,
  "aria-labelledby": PropTypes.string,
  "aria-required": PropTypes.bool,
};

TypeaheadCombobox.defaultProps = {
  listboxId: undefined,
  options: [],
  getOptionKey: (option, index) => option.id ?? index,
  getOptionDomId: (option, index) => `typeahead-option-${option.id ?? index}`,
  renderOption: (option) => option.label ?? option.name ?? "",
  onSelect: () => {},
  extraOptions: [],
  loading: false,
  openOnFocus: false,
  allowFreeText: false,
  onCommitFreeText: undefined,
  onInputFocusChange: undefined,
  expanded: undefined,
  onExpandedChange: undefined,
  placeholder: "",
  disabled: false,
  inputClassName: "usa-input",
  className: "",
  listClassName: "",
  optionClassName: "",
  highlightedClassName: "",
  loadingClassName: "",
  noResultsLabel: "",
  maxLength: undefined,
  "aria-invalid": undefined,
  "aria-describedby": undefined,
  "aria-labelledby": undefined,
  "aria-required": undefined,
};
